import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, Share2, Bookmark, BookMarked, Copy, Check,
  Send, Sparkles, X, Users, ChevronRight, Plus, MessageSquarePlus,
  Clock, Star, Pencil, Trash2, AlertTriangle, Zap, ChevronDown,
  CheckCircle2, Loader2, XCircle, Timer, ThumbsUp, ThumbsDown,
  MessageCircle,
} from 'lucide-react';

import { APP_CONFIG, getStreamUrl, createRequestBody } from '../config/appConfig';
import { useChatHistory, type ChatMessage } from '../hooks/useChatHistory';
import { useConversation, type ConversationMeta } from '../hooks/useConversation';
import { useSharing } from '../hooks/useSharing';
import { useRatings } from '../hooks/useRatings';
import { QUERY_TEMPLATES, CURRENT_USER } from '../data/dummyData';
import { useAuth } from '../contexts/AuthContext';
import type { StreamStep, StreamResult, SessionUsage } from '../types/rag';

export default function ChatPage() {
  const { user, hasPermission } = useAuth();
  const userId = user?.id || CURRENT_USER.id;
  const can = {
    newConversation: hasPermission('agente.nueva_conversacion'),
    sendMessage: hasPermission('agente.enviar_mensaje'),
    loadConversation: hasPermission('agente.cargar_conversacion'),
    rename: hasPermission('agente.renombrar_conversacion'),
    deleteConv: hasPermission('agente.eliminar_conversacion'),
    favorite: hasPermission('agente.favorito'),
    copy: hasPermission('agente.copiar'),
    download: hasPermission('agente.descargar'),
    share: hasPermission('agente.compartir'),
    tokens: hasPermission('agente.ver_tokens'),
    metadata: hasPermission('agente.ver_metadatos'),
    templates: hasPermission('agente.usar_plantillas'),
    rateResponse: hasPermission('calificaciones.calificar_respuesta'),
    rateSession: hasPermission('calificaciones.calificar_sesion'),
  };
  const location = useLocation();
  const navigate = useNavigate();
  const incomingConversationId = (location.state as { conversationId?: string })?.conversationId;

  const { messages, addMessage, clearHistory, setMessages } = useChatHistory({
    role: 'bot',
    text: APP_CONFIG.INITIAL_BOT_MESSAGE,
  });

  const {
    conversationId,
    isFavorite,
    conversations,
    ensureConversation,
    saveMessage,
    toggleFavorite,
    downloadConversation,
    deleteConversationById,
    renameConversation,
    loadConversations,
    loadConversation,
    startNewConversation,
    logActivity,
  } = useConversation(userId);

  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<StreamStep[]>([]);
  const [sessionUsage, setSessionUsage] = useState<SessionUsage>({ totalInput: 0, totalOutput: 0, calls: 0 });
  const [showUsagePanel, setShowUsagePanel] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [editingConvId, setEditingConvId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [shareLoading, setShareLoading] = useState(false);
  const [showSessionRating, setShowSessionRating] = useState(false);
  const [sessionScore, setSessionScore] = useState(0);
  const [sessionComment, setSessionComment] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    teamUsers,
    loadTeamUsers,
    shareConversation,
  } = useSharing(userId);

  const {
    ratings,
    savingRating,
    loadRatings,
    rateResponse,
    rateSession,
    resetRatings,
  } = useRatings(userId);

  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, steps]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const hasLoadedIncoming = useRef(false);
  useEffect(() => {
    if (incomingConversationId && !hasLoadedIncoming.current) {
      hasLoadedIncoming.current = true;
      (async () => {
        const data = await loadConversation(incomingConversationId);
        if (data?.messages) {
          const chatMessages: ChatMessage[] = data.messages.map((m: { id?: number; role: string; content: string; timestamp: string }) => ({
            id: m.id,
            role: m.role as 'user' | 'bot',
            text: m.content,
            timestamp: new Date(m.timestamp).getTime(),
          }));
          if (chatMessages.length > 0) setMessages(chatMessages);
        }
        loadRatings(incomingConversationId);
        navigate(location.pathname, { replace: true, state: {} });
      })();
    }
  }, [incomingConversationId, loadConversation, setMessages, navigate, location.pathname, loadRatings]);

  const resetSessionUsage = useCallback(() => {
    setSessionUsage({ totalInput: 0, totalOutput: 0, calls: 0 });
  }, []);

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    const q = msg.trim();
    if (!q) return;

    addMessage({ role: 'user', text: q });
    setMsg('');
    setLoading(true);
    setSteps([]);
    setElapsedMs(0);

    const startTime = Date.now();
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startTime), 100);

    try {
      const convId = await ensureConversation();
      await saveMessage(convId, 'user', q);

      const token = await (async () => {
        const { msalInstance, loginRequest } = await import('../config/authConfig');
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;
        try {
          const r = await msalInstance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
          return r.idToken;
        } catch {
          try { const r = await msalInstance.acquireTokenPopup(loginRequest); return r.idToken; }
          catch { return null; }
        }
      })();

      const res = await fetch(getStreamUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(createRequestBody(q, userId)),
      });

      if (!res.ok) {
        const errText = await res.text();
        let detail = errText;
        try { detail = JSON.parse(errText).detail || errText; } catch { /* use raw */ }
        if (res.status === 429) {
          throw new Error(`🚫 ${detail}`);
        }
        throw new Error(detail);
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      let resultData: StreamResult | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop()!;

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const raw = line.slice(6);
            try {
              const data = JSON.parse(raw);
              if (currentEventType === 'step') {
                setSteps(prev => {
                  const existing = prev.findIndex(s => s.id === data.id);
                  if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = data;
                    return updated;
                  }
                  return [...prev, data];
                });
              } else if (currentEventType === 'result') {
                resultData = data;
              }
            } catch { /* ignore malformed JSON */ }
          }
        }
      }

      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }

      const answer = resultData?.answer || '(Sin respuesta)';
      const metadata = {
        method: resultData?.method,
        response_time_sec: resultData?.response_time_sec,
        tokens: resultData?.tokens,
        rows_returned: resultData?.rows_returned,
      };

      if (resultData?.tokens) {
        setSessionUsage(prev => ({
          totalInput: prev.totalInput + (resultData!.tokens?.input || 0),
          totalOutput: prev.totalOutput + (resultData!.tokens?.output || 0),
          calls: prev.calls + 1,
        }));
      }

      const botMsgId = await saveMessage(convId, 'bot', answer, {
        method: resultData?.method,
        response_time_sec: resultData?.response_time_sec,
        rows_returned: resultData?.rows_returned,
        tokens: resultData?.tokens,
      });

      addMessage({ id: botMsgId ?? undefined, role: 'bot', text: answer, metadata } as ChatMessage);

      logActivity('query', q.slice(0, 100), user?.name || '', {
        method: resultData?.method,
        response_time_sec: resultData?.response_time_sec,
        tokens: resultData?.tokens,
      }, convId);
    } catch (err) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      const m = err instanceof Error ? err.message : String(err);
      let friendly = m;
      if (m.includes('Failed to fetch') || m.includes('ERR_CONNECTION')) {
        friendly = 'No se pudo conectar con el servidor. Verifica que el backend esté en ejecución.';
      }
      addMessage({ role: 'bot', text: `⚠️ ${friendly}` });
    } finally {
      setLoading(false);
      setSteps([]);
      loadConversations();
    }
  }

  function useTemplate(prompt: string) {
    setMsg(prompt);
    setShowTemplates(false);
    inputRef.current?.focus();
  }

  async function handleNewConversation() {
    startNewConversation();
    clearHistory();
    resetSessionUsage();
    resetRatings();
    setShowSessionRating(false);
    setSessionScore(0);
    setSessionComment('');
    setShowHistory(false);
    logActivity('new_conversation', 'Inició nueva conversación', user?.name || '');
  }

  async function handleRenameConversation(convId: string) {
    if (!editingTitle.trim()) return;
    await renameConversation(convId, editingTitle.trim());
    setEditingConvId(null);
    setEditingTitle('');
    loadConversations();
  }

  async function handleDeleteConversation(convId: string) {
    await deleteConversationById(convId);
    setDeleteConfirmId(null);
    if (convId === conversationId) {
      startNewConversation();
      clearHistory();
    }
    loadConversations();
    logActivity('delete_conversation', 'Eliminó una conversación', user?.name || '', undefined, convId);
  }

  async function handleLoadConversation(conv: ConversationMeta) {
    const data = await loadConversation(conv.id);
    if (data && data.messages) {
      const chatMessages: ChatMessage[] = data.messages.map((m: { id?: number; role: string; content: string; timestamp: string }) => ({
        id: m.id,
        role: m.role as 'user' | 'bot',
        text: m.content,
        timestamp: new Date(m.timestamp).getTime(),
      }));
      if (chatMessages.length > 0) {
        setMessages(chatMessages);
      }
    }
    loadRatings(conv.id);
    setShowHistory(false);
  }

  function handleDownload() {
    logActivity('download', 'Descargó conversación', user?.name || '', undefined, conversationId || undefined);
    if (conversationId) {
      downloadConversation();
    } else {
      const text = messages
        .map(m => `[${m.role === 'user' ? (user?.name || 'Usuario') : 'Asistente'}]\n${m.text}\n`)
        .join('\n---\n\n');
      const header = `Sesión de análisis - Apoyo Financiero\nFecha: ${new Date().toLocaleString('es-CO')}\nUsuario: ${user?.name || 'N/A'}\n${'='.repeat(50)}\n\n`;
      const blob = new Blob([header + text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analisis_af_${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  function copyChat() {
    const text = messages.map(m => `${m.role === 'user' ? '👤' : '🤖'} ${m.text}`).join('\n\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleShare() {
    if (!conversationId || selectedUsers.length === 0) return;
    setShareLoading(true);
    try {
      await shareConversation(conversationId, selectedUsers);
      setShared(true);
      logActivity('share', `Compartió conversación con ${selectedUsers.length} usuario(s)`, user?.name || '', undefined, conversationId);
      setTimeout(() => {
        setShared(false);
        setShowShareModal(false);
        setSelectedUsers([]);
      }, 1500);
    } catch {
      setShared(false);
    } finally {
      setShareLoading(false);
    }
  }

  function toggleUser(id: string) {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  }

  function openShareModal() {
    setShowShareModal(true);
    loadTeamUsers();
  }

  async function handleRateResponse(messageId: number, score: number) {
    if (!conversationId) return;
    await rateResponse(conversationId, messageId, score);
  }

  async function handleRateSession() {
    if (!conversationId || sessionScore === 0) return;
    await rateSession(conversationId, sessionScore, sessionComment);
    setShowSessionRating(false);
  }

  const hasRealMessages = messages.length > 1;

  return (
    <div className="flex gap-4 h-[calc(100vh-7.5rem)]">
      {/* Panel lateral izquierdo - Historial + Templates */}
      <div className="hidden xl:flex flex-col w-72 flex-shrink-0 gap-3">
        {can.newConversation && (
          <button
            onClick={handleNewConversation}
            className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-white text-sm font-medium transition-all hover:shadow-lg active:scale-[0.98]"
            style={{ backgroundColor: '#1B2A4A' }}
          >
            <MessageSquarePlus className="w-4 h-4" />
            Nueva conversación
          </button>
        )}

        {/* Historial de conversaciones */}
        <div className="bg-white rounded-xl border border-gray-100 flex-1 overflow-hidden flex flex-col" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">Historial</h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 ml-auto">
              {conversations.length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
            {conversations.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Sin conversaciones previas</p>
            ) : (
              conversations.map(conv => (
                <div key={conv.id} className="relative group">
                  {editingConvId === conv.id ? (
                    <div className="p-2 rounded-lg border border-yellow-300 bg-yellow-50/60">
                      <input
                        autoFocus
                        value={editingTitle}
                        onChange={e => setEditingTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRenameConversation(conv.id);
                          if (e.key === 'Escape') setEditingConvId(null);
                        }}
                        onBlur={() => handleRenameConversation(conv.id)}
                        className="w-full text-xs px-2 py-1 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => handleLoadConversation(conv)}
                      className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                        conv.id === conversationId
                          ? 'border-yellow-300 bg-yellow-50/60'
                          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate pr-10">{conv.titulo}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {new Date(conv.fecha_actualizacion).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                            {' · '}
                            {conv.message_count} msgs
                          </p>
                        </div>
                        {conv.es_favorita && (
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                    </button>
                  )}
                  {editingConvId !== conv.id && (can.rename || can.deleteConv) && (
                    <div className="absolute top-1.5 right-1.5 hidden group-hover:flex items-center gap-0.5 bg-white/90 rounded-md shadow-sm border border-gray-100 px-0.5">
                      {can.rename && (
                        <button
                          onClick={e => { e.stopPropagation(); setEditingConvId(conv.id); setEditingTitle(conv.titulo); }}
                          className="p-1 rounded hover:bg-gray-100 transition-colors"
                          title="Renombrar"
                        >
                          <Pencil className="w-3 h-3 text-gray-400" />
                        </button>
                      )}
                      {can.deleteConv && (
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteConfirmId(conv.id); }}
                          className="p-1 rounded hover:bg-red-50 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Templates */}
        {can.templates && <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)', maxHeight: '40%' }}>
          <div className="flex items-center gap-2 px-4 pt-3 pb-2">
            <Sparkles className="w-4 h-4" style={{ color: '#EDAA00' }} />
            <h3 className="text-xs font-semibold text-gray-900">Consultas sugeridas</h3>
          </div>
          <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
            {QUERY_TEMPLATES.map(t => (
              <button
                key={t.id}
                onClick={() => useTemplate(t.prompt)}
                className="w-full text-left p-2 rounded-lg border border-gray-100 hover:border-yellow-200 hover:bg-yellow-50/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-gray-800 group-hover:text-gray-900">{t.title}</span>
                  <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-yellow-500 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>}
      </div>

      {/* Chat principal */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white rounded-xl border border-gray-100 flex-1 flex flex-col overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <h2 className="text-sm font-semibold text-gray-900">Asistente de Apoyo Financiero</h2>
              {conversationId && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
                  Guardando
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {can.favorite && (
                <button
                  onClick={toggleFavorite}
                  disabled={!conversationId}
                  className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-30"
                  title={isFavorite ? 'Quitar de favoritos' : 'Guardar en favoritos'}
                >
                  {isFavorite
                    ? <BookMarked className="w-4 h-4 text-yellow-500" />
                    : <Bookmark className="w-4 h-4 text-gray-400" />}
                </button>
              )}
              {can.copy && (
                <button
                  onClick={copyChat}
                  disabled={!hasRealMessages}
                  className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-30"
                  title="Copiar conversación"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </button>
              )}
              {can.download && (
                <button
                  onClick={handleDownload}
                  disabled={!hasRealMessages}
                  className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-30"
                  title="Descargar conversación"
                >
                  <Download className="w-4 h-4 text-gray-400" />
                </button>
              )}
              {can.share && (
                <button
                  onClick={openShareModal}
                  disabled={!hasRealMessages || !conversationId}
                  className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-30"
                  title="Compartir con equipo"
                >
                  <Share2 className="w-4 h-4 text-gray-400" />
                </button>
              )}
              {can.tokens && sessionUsage.calls > 0 && (
                <button
                  onClick={() => setShowUsagePanel(!showUsagePanel)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-yellow-50 transition-colors text-xs"
                  title="Consumo de la sesión"
                >
                  <Zap className="w-3.5 h-3.5" style={{ color: '#EDAA00' }} />
                  <span className="text-gray-600 font-medium hidden sm:inline">
                    {(sessionUsage.totalInput + sessionUsage.totalOutput).toLocaleString()} tokens
                  </span>
                  <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${showUsagePanel ? 'rotate-180' : ''}`} />
                </button>
              )}

              <div className="w-px h-4 bg-gray-200 mx-1" />
              {/* Nueva conversación (mobile) */}
              <button
                onClick={handleNewConversation}
                className="p-1.5 rounded-lg hover:bg-emerald-50 transition-colors xl:hidden"
                title="Nueva conversación"
              >
                <Plus className="w-4 h-4 text-gray-400 hover:text-emerald-600" />
              </button>
              {/* Historial (mobile) */}
              <button
                onClick={() => { setShowHistory(!showHistory); loadConversations(); }}
                className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors xl:hidden"
                title="Historial"
              >
                <Clock className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Token usage panel */}
          <AnimatePresence>
            {showUsagePanel && sessionUsage.calls > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden border-b border-gray-100"
              >
                <div className="px-4 py-3 bg-gradient-to-r from-yellow-50/80 to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4" style={{ color: '#EDAA00' }} />
                    <span className="text-xs font-semibold text-gray-800">Consumo de la sesión</span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Llamadas al modelo</p>
                      <p className="text-sm font-bold text-gray-900">{sessionUsage.calls}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Tokens entrada</p>
                      <p className="text-sm font-bold text-gray-900">{sessionUsage.totalInput.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Tokens salida</p>
                      <p className="text-sm font-bold text-gray-900">{sessionUsage.totalOutput.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total tokens</p>
                      <p className="text-sm font-bold" style={{ color: '#EDAA00' }}>{(sessionUsage.totalInput + sessionUsage.totalOutput).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile: Templates toggle */}
          {can.templates && <div className="xl:hidden px-4 py-2 border-b border-gray-100">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#EDAA00' }} />
              {showTemplates ? 'Ocultar sugerencias' : 'Ver consultas sugeridas'}
            </button>
            <AnimatePresence>
              {showTemplates && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-2 gap-2 mt-2 pb-1">
                    {QUERY_TEMPLATES.slice(0, 4).map(t => (
                      <button key={t.id} onClick={() => useTemplate(t.prompt)}
                        className="text-left p-2 rounded-lg border border-gray-100 hover:border-yellow-200 hover:bg-yellow-50/50 transition-all text-xs text-gray-700">
                        {t.title}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>}

          {/* Mobile: Historial panel */}
          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden xl:hidden border-b border-gray-100"
              >
                <div className="p-3 max-h-48 overflow-y-auto space-y-1.5">
                  {conversations.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">Sin conversaciones previas</p>
                  ) : conversations.slice(0, 10).map(conv => (
                    <button
                      key={conv.id}
                      onClick={() => handleLoadConversation(conv)}
                      className="w-full text-left p-2 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all"
                    >
                      <p className="text-xs font-medium text-gray-800 truncate">{conv.titulo}</p>
                      <p className="text-[10px] text-gray-400">{conv.message_count} msgs</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Messages */}
          <div ref={messagesRef} className="flex-1 overflow-y-auto p-4 space-y-4" style={{ backgroundColor: '#F8FAFC' }}>
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                role={m.role}
                text={m.text}
                userName={user?.name}
                timestamp={m.timestamp}
                metadata={m.metadata}
                showMeta={can.metadata}
                messageId={m.id}
                rating={m.id ? ratings.responses[m.id] : undefined}
                onRate={can.rateResponse && m.id ? (score: number) => handleRateResponse(m.id!, score) : undefined}
              />
            ))}
            {loading && (
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: '#1B2A4A' }}>AF</div>
                <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 min-w-[260px] max-w-[400px]">
                  {steps.length === 0 ? (
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Timer className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[11px] font-medium text-gray-500">{(elapsedMs / 1000).toFixed(1)}s</span>
                      </div>
                      {steps.map((step, i) => (
                        <motion.div
                          key={step.id + '-' + i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex items-start gap-2"
                        >
                          {step.status === 'done' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          ) : step.status === 'error' ? (
                            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Loader2 className="w-4 h-4 text-yellow-500 animate-spin flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs leading-snug ${
                              step.status === 'done' ? 'text-gray-500' :
                              step.status === 'error' ? 'text-red-600' :
                              'text-gray-800 font-medium'
                            }`}>
                              {step.label}
                            </p>
                            {step.status === 'done' && (
                              <span className="text-[10px] text-gray-400">{(step.elapsed_ms / 1000).toFixed(1)}s</span>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Session Rating */}
          {can.rateSession && conversationId && hasRealMessages && (
            <div className="border-t border-gray-100 bg-white">
              {!showSessionRating && !ratings.session ? (
                <button
                  onClick={() => setShowSessionRating(true)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-50/50 transition-colors"
                >
                  <Star className="w-3.5 h-3.5" /> Calificar esta sesión
                </button>
              ) : ratings.session && !showSessionRating ? (
                <div className="flex items-center justify-center gap-2 py-2">
                  <span className="text-[11px] text-gray-400">Tu calificación:</span>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-3.5 h-3.5 ${s <= ratings.session!.score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                    ))}
                  </div>
                  <button onClick={() => { setShowSessionRating(true); setSessionScore(ratings.session.score); setSessionComment(ratings.session.comentario || ''); }}
                    className="text-[10px] text-gray-400 hover:text-gray-600 ml-2 underline">Editar</button>
                </div>
              ) : null}

              <AnimatePresence>
                {showSessionRating && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 py-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">¿Cómo fue tu experiencia en esta sesión?</span>
                        <button onClick={() => setShowSessionRating(false)} className="p-1 rounded hover:bg-gray-100">
                          <X className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(s => (
                          <button key={s} onClick={() => setSessionScore(s)} className="p-1 transition-transform hover:scale-110">
                            <Star className={`w-6 h-6 transition-colors ${s <= sessionScore ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 hover:text-yellow-200'}`} />
                          </button>
                        ))}
                        <span className="text-xs text-gray-400 ml-2">
                          {sessionScore === 1 ? 'Muy mala' : sessionScore === 2 ? 'Mala' : sessionScore === 3 ? 'Regular' : sessionScore === 4 ? 'Buena' : sessionScore === 5 ? 'Excelente' : ''}
                        </span>
                      </div>
                      <textarea
                        value={sessionComment}
                        onChange={e => setSessionComment(e.target.value)}
                        placeholder="Comentario opcional: ¿qué podría mejorar?"
                        rows={2}
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                        style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                      />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setShowSessionRating(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 rounded-lg">Cancelar</button>
                        <button
                          onClick={handleRateSession}
                          disabled={sessionScore === 0 || savingRating}
                          className="px-4 py-1.5 text-xs font-medium text-white rounded-lg disabled:opacity-40 transition-all"
                          style={{ backgroundColor: '#1B2A4A' }}
                        >
                          {savingRating ? 'Guardando...' : 'Enviar calificación'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Input */}
          {can.sendMessage && (
            <form onSubmit={onSend} className="border-t border-gray-100 p-3 bg-white">
              <div className="flex gap-2 items-center">
                <input
                  ref={inputRef}
                  value={msg}
                  onChange={e => setMsg(e.target.value)}
                  placeholder={APP_CONFIG.INPUT_PLACEHOLDER}
                  autoComplete="off"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-gray-50/50"
                  style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                />
                <button
                  type="submit"
                  disabled={loading || !msg.trim()}
                  className="p-2.5 rounded-xl text-white disabled:opacity-40 transition-all hover:shadow-md"
                  style={{ backgroundColor: '#1B2A4A' }}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmId && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirmId(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar conversación</h3>
                <p className="text-sm text-gray-500 mb-6">
                  ¿Estás seguro de que deseas eliminar esta conversación? Esta acción no se puede deshacer.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleDeleteConversation(deleteConfirmId)}
                    className="px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" style={{ color: '#EDAA00' }} />
                  <h3 className="text-lg font-semibold text-gray-900">Compartir análisis</h3>
                </div>
                <button onClick={() => setShowShareModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-5">
                <p className="text-sm text-gray-500 mb-4">Selecciona los miembros del equipo con quienes deseas compartir esta sesión de análisis.</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {teamUsers.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-6">No hay usuarios disponibles</p>
                  ) : teamUsers.map(u => {
                    const avatar = ((u.nombre?.[0] || '') + (u.apellido?.[0] || '')).toUpperCase() || '??';
                    const fullName = `${u.nombre} ${u.apellido}`.trim();
                    return (
                      <label key={u.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        selectedUsers.includes(u.id) ? 'border-yellow-300 bg-yellow-50/50' : 'border-gray-100 hover:border-gray-200'
                      }`}>
                        <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUser(u.id)} className="sr-only" />
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white" style={{ backgroundColor: '#1B2A4A' }}>
                          {avatar}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{fullName}</p>
                          <p className="text-xs text-gray-500">{u.email} · {u.departamento}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedUsers.includes(u.id) ? 'border-yellow-500 bg-yellow-500' : 'border-gray-300'
                        }`}>
                          {selectedUsers.includes(u.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between p-5 border-t border-gray-100">
                <span className="text-xs text-gray-400">{selectedUsers.length} seleccionado(s)</span>
                <div className="flex gap-2">
                  <button onClick={() => setShowShareModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">
                    Cancelar
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={selectedUsers.length === 0 || shared || shareLoading}
                    className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 flex items-center gap-2 transition-all"
                    style={{ backgroundColor: shared ? '#10B981' : '#1B2A4A' }}
                  >
                    {shared ? <><Check className="w-4 h-4" /> Compartido</> : shareLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Compartiendo...</> : <><Users className="w-4 h-4" /> Compartir</>}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatTimestamp(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: true,
  });
}

function MessageBubble({ role, text, userName, timestamp, metadata, showMeta: showMetaPerm = true, messageId, rating, onRate }: {
  role: ChatMessage['role']; text: string; userName?: string; timestamp?: number;
  metadata?: ChatMessage['metadata'];
  showMeta?: boolean;
  messageId?: number;
  rating?: number;
  onRate?: (score: number) => void;
}) {
  const isUser = role === 'user';
  const time = formatTimestamp(timestamp);
  const [metaOpen, setMetaOpen] = useState(false);
  const hasMeta = showMetaPerm && !isUser && metadata && (metadata.response_time_sec || metadata.tokens);
  const canRate = !isUser && onRate && messageId;

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-start gap-2.5 ${isUser ? 'justify-end' : ''}`}>
        {!isUser && (
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: '#1B2A4A' }}>AF</div>
        )}
        <div className={`max-w-[70%] rounded-xl text-sm leading-relaxed ${
          isUser
            ? 'text-white rounded-br-sm px-4 py-3'
            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
        }`} style={isUser ? { backgroundColor: '#1B2A4A' } : {}}>
          {isUser ? (
            <span>{text}</span>
          ) : (
            <>
              <div className="prose prose-sm max-w-none prose-p:mb-2 prose-p:last:mb-0 prose-li:mb-0.5 px-4 py-3">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    a: (props) => <a {...props} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 underline" />,
                    p: (props) => <p {...props} className="mb-2 last:mb-0" />,
                    ul: (props) => <ul {...props} className="mb-2 last:mb-0 pl-4 list-disc" />,
                    ol: (props) => <ol {...props} className="mb-2 last:mb-0 pl-4 list-decimal" />,
                    li: (props) => <li {...props} className="mb-0.5" />,
                    strong: (props) => <strong {...props} className="font-semibold" />,
                    code: (props) => {
                      const { className, children, ...rest } = props;
                      const isInline = !className;
                      return isInline
                        ? <code {...rest} className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                        : <code {...rest} className={className}>{children}</code>;
                    },
                  }}
                >
                  {text}
                </ReactMarkdown>
              </div>
              {/* Rating + Meta bar */}
              <div className="border-t border-gray-100 flex items-center">
                {canRate && (
                  <div className="flex items-center gap-0.5 px-3 py-1.5">
                    <button
                      onClick={() => onRate(1)}
                      className={`p-1 rounded transition-colors ${rating === 1 ? 'bg-emerald-50 text-emerald-500' : 'text-gray-300 hover:text-emerald-400 hover:bg-emerald-50/50'}`}
                      title="Respuesta útil"
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onRate(0)}
                      className={`p-1 rounded transition-colors ${rating === 0 ? 'bg-red-50 text-red-400' : 'text-gray-300 hover:text-red-400 hover:bg-red-50/50'}`}
                      title="Respuesta no útil"
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {hasMeta && (
                  <button
                    onClick={() => setMetaOpen(!metaOpen)}
                    className="flex items-center gap-1.5 px-3 py-1.5 flex-1 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50/50 transition-colors"
                  >
                    <Zap className="w-3 h-3" />
                    <span>{metadata.response_time_sec?.toFixed(1)}s</span>
                    {metadata.method && <span className="px-1 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">{metadata.method}</span>}
                    {metadata.tokens && (metadata.tokens.input + metadata.tokens.output) > 0 && (
                      <span>{(metadata.tokens.input + metadata.tokens.output).toLocaleString()} tokens</span>
                    )}
                    <ChevronDown className={`w-2.5 h-2.5 ml-auto transition-transform ${metaOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
              <AnimatePresence>
                {metaOpen && hasMeta && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                      {metadata.response_time_sec != null && (
                        <div><span className="text-gray-400">Tiempo:</span> <span className="text-gray-600 font-medium">{metadata.response_time_sec.toFixed(2)}s</span></div>
                      )}
                      {metadata.method && (
                        <div><span className="text-gray-400">Método:</span> <span className="text-gray-600 font-medium">{metadata.method.toUpperCase()}</span></div>
                      )}
                      {metadata.tokens && metadata.tokens.input > 0 && (
                        <div><span className="text-gray-400">Tokens entrada:</span> <span className="text-gray-600 font-medium">{metadata.tokens.input.toLocaleString()}</span></div>
                      )}
                      {metadata.tokens && metadata.tokens.output > 0 && (
                        <div><span className="text-gray-400">Tokens salida:</span> <span className="text-gray-600 font-medium">{metadata.tokens.output.toLocaleString()}</span></div>
                      )}
                      {metadata.rows_returned != null && metadata.rows_returned > 0 && (
                        <div><span className="text-gray-400">Filas:</span> <span className="text-gray-600 font-medium">{metadata.rows_returned.toLocaleString()}</span></div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
        {isUser && (
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0" style={{ backgroundColor: '#EDAA00' }}>
            {userName?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
          </div>
        )}
      </div>
      {time && (
        <span className={`text-[10px] text-gray-400 mt-1 ${isUser ? 'mr-11' : 'ml-11'}`}>
          {time}
        </span>
      )}
    </div>
  );
}
